import { reaction } from 'mobx';

import { equalsDeep } from 'src/shared/utils/equals-deep';
import type { RootStore } from 'src/store';
import type { WellFormManager } from 'src/store/well-form-manager/well-form-manager';

import type { FormStore } from '../entities/form.entity';

import { FormPlugin } from './abstract-form-plugin.entity';

/**
 * Поля, значения которых идентифицируют саму скважину и потому должны быть
 * одинаковыми для всех геозаданий одной скважины. В UI они доступны для
 * редактирования только в "первом" геозадании; во всех остальных формах
 * визуально заблокированы, а их значения автоматически синхронизируются
 * со значениями первой формы.
 */
const SYNCED_FIELD_IDS = ['wellName', 'pad', 'coordinatesT2X', 'coordinatesT2Y', 'coordinatesT3X', 'coordinatesT3Y'];

/**
 * Используется на форме редактирования скважины с историей геозаданий
 * (см. {@link WellFormManagerWithGeoTasksHistory}). Подключается к каждой
 * созданной форме; для НЕ-первой формы:
 *
 *  1. Визуально блокирует поля идентификации скважины
 *     (`wellName`, `pad`, координаты T2/T3) — пользователь не должен
 *     редактировать их в "зависимых" геозаданиях.
 *  2. Подписывается на изменения значений этих полей в первой (primary) форме
 *     и зеркалит их в текущую форму через `form.onValueChange(...)`, что
 *     публикует `mainDataValueChange` в `eventBus` и корректно прогоняет
 *     значение через всю плагинную/сервисную цепочку.
 *
 * Для самой primary-формы плагин является no-op (поля доступны, синхронизация
 * не запускается).
 */
export class PrimaryGeoTaskFieldsSyncPlugin extends FormPlugin<FormStore> {
  private readonly manager: WellFormManager;

  constructor(rootStore: RootStore, manager: WellFormManager) {
    super(rootStore);
    this.manager = manager;
  }

  connect(form: FormStore): VoidFunction {
    this.form = form;

    const disposers: VoidFunction[] = [];

    // 1) Управление visual-блокировкой синхронизируемых полей.
    //    Поля разблокированы только в primary-форме (formList[0]).
    //    `manager.formList` — observable, reaction среагирует на любую перестановку.
    disposers.push(
      reaction(
        () => this.manager.formList[0] === form,
        (isPrimary) => {
          for (const fieldId of SYNCED_FIELD_IDS) {
            const item = form.fields[fieldId];

            if (!item) {
              continue;
            }

            item.setIsVisuallyDisabled({ flagId: this.pluginId, value: !isPrimary });
          }
        },
        { fireImmediately: true },
      ),
    );

    // 2) Первичная синхронизация значений из primary-формы.
    //    Один раз на момент подключения плагина: значения не-primary формы
    //    из БД могут отличаться от primary — приводим их к primary.
    //    Дальнейшие изменения подхватываются через подписку (3).
    this.syncFromPrimary(form);

    // 3) Ретрансляция изменений: подписываемся на СВОЮ форму.
    //    Если эта форма — primary, любое изменение синхронизируемого поля
    //    мы дублируем во все остальные формы через `otherForm.onValueChange`.
    //    Так все изменения данных идут только через публикацию событий,
    //    cross-form реакций нет, а после удаления первого геозадания новая
    //    primary-форма сама начнёт ретранслировать (её плагин уже подписан
    //    на свою же шину).
    disposers.push(
      form.eventBus.subscribe('mainDataValueChange', ({ fieldId, value }) => {
        if (this.manager.formList[0] !== form) {
          return;
        }

        if (!SYNCED_FIELD_IDS.includes(fieldId)) {
          return;
        }

        for (const otherForm of this.manager.formList) {
          if (otherForm === form) {
            continue;
          }

          const targetItem = otherForm.fields[fieldId];

          if (!targetItem) {
            continue;
          }

          if (equalsDeep(value, targetItem.value)) {
            continue;
          }

          otherForm.onValueChange(fieldId, value, { control: targetItem });
        }
      }),
    );

    return () => {
      disposers.forEach((dispose) => dispose());
    };
  }

  /**
   * One-shot: подтягивает значения синхронизируемых полей из текущей primary-формы
   * в переданную форму через `form.onValueChange` (т. е. также через публикацию
   * `mainDataValueChange`). Если форма сама является primary — no-op.
   */
  private syncFromPrimary(form: FormStore): void {
    const primary = this.manager.formList[0];

    if (!primary || primary === form) {
      return;
    }

    for (const fieldId of SYNCED_FIELD_IDS) {
      const primaryItem = primary.fields[fieldId];
      const ownItem = form.fields[fieldId];

      if (!primaryItem || !ownItem) {
        continue;
      }

      if (equalsDeep(primaryItem.value, ownItem.value)) {
        continue;
      }

      form.onValueChange(fieldId, primaryItem.value, { control: ownItem });
    }
  }
}
